import React, { useEffect, useRef, useState } from 'react';
import './WaitlistModal.scss';

interface WaitlistModalProps { isOpen: boolean; onClose: () => void; initialEmail?: string; }

export const WaitlistModal: React.FC<WaitlistModalProps> = ({ isOpen, onClose, initialEmail = '' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('writer');
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  const submitting = useRef(isSubmitting);
  close.current = onClose;
  submitting.current = isSubmitting;

  useEffect(() => {
    if (!isOpen) return;
    setEmail(initialEmail);
    setError('');
    setIsSuccess(false);
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting.current) { event.preventDefault(); close.current(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), a[href]'
      ) || []);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); dialog.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, initialEmail]);

  useEffect(() => {
    if (isOpen && isSuccess) dialog.current?.focus();
  }, [isOpen, isSuccess]);

  if (!isOpen) return null;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await window.fetch('/api/waitlist', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, role, website }),
      });
      const data = await response.json();
      if (!response.ok || data.success !== true) throw new Error(data.error || 'We could not save your signup. Please try again.');
      setIsSuccess(true);
    } catch (failure: any) { setError(failure.message || 'We could not save your signup. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="waitlist-overlay" onClick={() => !isSubmitting && onClose()}>
      <div className="waitlist-dialog" ref={dialog} tabIndex={-1} role="dialog" aria-modal="true"
        aria-labelledby="waitlist-title" aria-describedby="waitlist-description" onClick={event => event.stopPropagation()}>
        <button className="waitlist-close" type="button" aria-label="Close waitlist form" disabled={isSubmitting} onClick={onClose}>×</button>
        {isSuccess ? <div className="waitlist-success" role="status">
          <span className="waitlist-success__mark" aria-hidden="true">✓</span>
          <h2 id="waitlist-title">You're on the list.</h2>
          <p id="waitlist-description">Your signup is saved. We'll email you when we're ready to welcome you.</p>
          <button className="waitlist-submit" type="button" onClick={onClose}>Done</button>
        </div> : <>
          <p className="waitlist-eyebrow">In development · Early interest</p>
          <h2 id="waitlist-title">Join the waitlist</h2>
          <p id="waitlist-description">Tell us a little about you. We'll keep your place and send news about launch.</p>
          <form onSubmit={submit} aria-busy={isSubmitting}>
            <div className="waitlist-honeypot" aria-hidden="true">
              <label htmlFor="waitlist-website">Website</label>
              <input id="waitlist-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                value={website} onChange={event => setWebsite(event.target.value)} />
            </div>
            <label htmlFor="waitlist-name">Name</label>
            <input id="waitlist-name" name="name" type="text" autoComplete="given-name" required maxLength={100}
              value={name} disabled={isSubmitting} onChange={event => setName(event.target.value)} />
            <label htmlFor="waitlist-email">Email address</label>
            <input id="waitlist-email" name="email" type="email" autoComplete="email" required maxLength={254}
              value={email} disabled={isSubmitting} onChange={event => setEmail(event.target.value)} />
            <label htmlFor="waitlist-role">I'm interested as a…</label>
            <select id="waitlist-role" name="role" value={role} disabled={isSubmitting} onChange={event => setRole(event.target.value)}>
              <option value="writer">Writer</option><option value="reader">Beta reader</option><option value="agent">Literary agent</option>
            </select>
            <p className="waitlist-privacy">We’ll use your details to manage the waitlist and send your confirmation and launch updates. <a href="mailto:contact@manuscriptmatch.com">Contact us</a> if you’d like your details removed.</p>
            {error && <p className="waitlist-error" role="alert">{error}</p>}
            <button className="waitlist-submit" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving your place…' : 'Join the waitlist'}</button>
          </form>
        </>}
      </div>
    </div>
  );
};
